import { Progress, Tooltip } from 'antd'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useGeckoNotifications } from 'features/notifications'
import React, { FC, useMemo } from 'react'

interface TicketProgressBarProps {
  ticket: LibraTicket
  targetStateId?: string
  workflow: LibraWorkflow
  circle?: boolean
}

// Color gradient ranges for orange (low progress) to green (high progress)
// Orange: rgb(255, 165, 0) or hsl(39, 100%, 50%)
// Green: rgb(82, 196, 26) or hsl(103, 77%, 44%)
const HUE_RANGE = [39, 103] as const // Orange to green
const SATURATION_RANGE = [100, 77] as const
const LIGHTNESS_RANGE = [50, 44] as const

/**
 * Converts a ratio (0-1) to a color gradient from orange to green
 */
const ratioToColor = (ratio: number): string => {
  const clampedRatio = Math.max(0, Math.min(1, ratio))

  const [hueMin, hueMax] = HUE_RANGE
  const [saturationMin, saturationMax] = SATURATION_RANGE
  const [lightnessMin, lightnessMax] = LIGHTNESS_RANGE

  // Interpolate between orange and green using HSL for smooth transition
  const hue = hueMin + clampedRatio * (hueMax - hueMin)
  const saturation =
    saturationMin + clampedRatio * (saturationMax - saturationMin)
  const lightness = lightnessMin + clampedRatio * (lightnessMax - lightnessMin)

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

const RawTicketProgressBar: FC<TicketProgressBarProps> = ({
  ticket,
  targetStateId,
  workflow,
  circle = false,
}) => {
  const { errorNotification } = useGeckoNotifications()

  const { tooltipTitle, completionRatio, color, isComplete } = useMemo(() => {
    const targetStepIdx = targetStateId
      ? workflow.stepIndices.get(targetStateId)
      : undefined
    if (targetStateId && targetStepIdx === undefined) {
      const msg = `Config target state ID ${targetStateId} not found in workflow`
      errorNotification(msg)
      console.error(msg)
    }

    const currentIndex = workflow.stepIndices.get(ticket.stepId) ?? -1
    const total = targetStepIdx ?? workflow.steps.length
    const currentStep = workflow.steps[currentIndex]
    const complete = ticket.workflowComplete
    // Progress toward `total` (the config target step when set, else the
    // full workflow length) from values already resolved above.
    const ratio = complete
      ? 1
      : currentIndex < 0 || total <= 0
        ? 0
        : currentIndex / total
    const title = complete
      ? 'Complete'
      : currentIndex >= total
        ? 'Complete'
        : `${currentStep?.name ?? 'Unknown'} (${currentIndex + 1}/${total})`

    return {
      tooltipTitle: title,
      completionRatio: ratio,
      color: ratioToColor(ratio),
      isComplete: !!complete,
    }
  }, [errorNotification, targetStateId, ticket, workflow])

  return (
    <Tooltip title={tooltipTitle}>
      <Progress
        type={circle ? 'circle' : 'line'}
        percent={completionRatio * 100}
        size={circle ? 40 : 'small'}
        strokeColor={color}
        status={isComplete ? 'success' : 'active'}
        showInfo={false}
      />
    </Tooltip>
  )
}

export const TicketProgressBar = React.memo(RawTicketProgressBar)
